<script setup lang="ts">
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { Check, RefreshCw, Activity } from 'lucide-vue-next'
import { useFormatters } from '@lib/composables/useFormatters'
import EmptyState from '@lib/components/shared/EmptyState.vue'
import type { DashboardScanLog } from './dashboard-process'

const props = defineProps<{
    scans: DashboardScanLog[]
    refreshing: boolean
    lastFetchedAt: Date | null
}>()

defineEmits<{ refresh: [] }>()

const { t } = useI18n()
const { dateTime } = useFormatters()

const updatedSeconds = computed(() => {
    if (!props.lastFetchedAt) return null
    return Math.floor((Date.now() - props.lastFetchedAt.getTime()) / 1000)
})
</script>

<template>
    <section class="mb-6">
        <div class="bg-white rounded-xl shadow-md border border-gray-200 overflow-hidden">
            <div class="flex items-center gap-3 px-4 py-3 border-b border-gray-200 flex-wrap">
                <div class="w-6 h-6 rounded-lg bg-teal/10 flex items-center justify-center">
                    <Activity class="w-3.5 h-3.5 text-teal" />
                </div>
                <span class="text-sm font-semibold text-ink">{{ t('dashboard.feed.title') }}</span>
                <span class="text-xs text-gray-500 ml-1">{{ scans.length }}</span>
                <div class="ml-auto flex items-center gap-2">
                    <span v-if="updatedSeconds !== null" class="text-[11px] text-gray-500">
                        {{ t('dashboard.updatedAgo', { seconds: updatedSeconds }) }}
                    </span>
                    <button type="button" :disabled="refreshing"
                        class="p-1.5 rounded-md text-gray-500 hover:text-teal hover:bg-teal/10 disabled:opacity-50"
                        @click="$emit('refresh')">
                        <RefreshCw class="w-4 h-4" :class="{ 'animate-spin': refreshing }" />
                    </button>
                </div>
            </div>
            <div v-if="scans.length === 0" class="p-6">
                <EmptyState :title="t('dashboard.feed.empty')" />
            </div>
            <ul v-else class="divide-y divide-gray-100">
                <li v-for="s in scans" :key="s.id" class="flex items-center gap-3 px-4 py-2.5">
                    <Check class="w-4 h-4 text-green-600 shrink-0" />
                    <div class="flex-1 min-w-0 grid grid-cols-3 gap-2 text-sm">
                        <span class="text-gray-700 truncate">{{ s.deviceName || '—' }}</span>
                        <span class="text-ink font-mono truncate">{{ s.ticketCode }}</span>
                        <span class="text-gray-500 truncate">{{ s.eventName }}</span>
                    </div>
                    <span class="text-[11px] text-gray-400 whitespace-nowrap">{{ dateTime(s.createdAt) }}</span>
                </li>
            </ul>
        </div>
    </section>
</template>
